/**
 * Which subscription UI a session gets: `control` (the production one) or `b` (the
 * experiment copies in this folder).
 *
 * Resolution order, first match wins:
 *   1. `?sub-update=b` / `?sub-update=control` in the URL — written to sessionStorage at import
 *      time, so it survives the reloads the wizard does after a successful charge but dies with
 *      the tab. NON-PROD ONLY.
 *   2. sessionStorage `scio3_sub_update_arm` — whatever a previous `?sub-update` set, this tab.
 *   3. The DECIDER's arm, pushed in by `useBillingExperimentArm` via `setDecidedArm`
 *      once `resolveExperimentVariant` settles. Server-side, keyed on companyId,
 *      persisted write-once to `company.experiments['sub-update-v1']`, so a company's
 *      arm never flips. This is what a real 50/50 runs on.
 *   4. `VITE_ENABLE_BILLING_EXPERIMENT` — build-time default, and the only thing
 *      answering before the decider settles or when nobody is enrolled.
 *
 * The two overrides sit ABOVE the decider on purpose: they are how a reviewer pins an
 * arm, and they must beat whatever the company is actually enrolled in. Normal users set
 * neither, so for them this reads as "decider, else build default".
 *
 * A pin still masks a live experiment while it is set, so `warnPinned` logs on every page load
 * where a pin is what answered. Do not remove it: this cost real debugging time on qa3g.
 *
 * It used to be localStorage, which made the masking PERMANENT and per-browser-profile rather than
 * per-tab — one `?sub-update=b` visit weeks earlier meant every account on that machine served arm
 * b while the dashboard reported a healthy split. `useWelcomeScreenVariant` (the PostHog-driven
 * experiment) had it right with sessionStorage; this one now matches. Any leftover localStorage key
 * is actively deleted on import — see `migrateLegacyPin`.
 *
 * Why a module-level value rather than a hook everywhere: the arm picks between two
 * COMPONENT TREES (`experiment/**` against production's), and those switch points —
 * AppShell's paywall, BillingDialogs' wizard, two nav entries — are plain conditionals
 * in render. One resolve at the top of the app feeds all of them, and the two that can
 * actually flash hold until it settles. See `useBillingExperimentArm`.
 */

export const SUB_UPDATE_ARMS = {
  CONTROL: 'control',
  B: 'b',
} as const;

export type SubUpdateArm = (typeof SUB_UPDATE_ARMS)[keyof typeof SUB_UPDATE_ARMS];

const STORAGE_KEY = 'scio3_sub_update_arm';
const QUERY_PARAM = 'sub-update';

/**
 * Cache of the DECIDER's answer — not an override, and nothing to do with the QA pin above.
 *
 * The frontend's "sticky" short-circuit reads `company.experiments[key]`, but assignments live in
 * Postgres (`optigrowth_assignments`); that Mongo field is legacy and the backend does not write
 * it. So `storedVariant` is null on every load, the decider is re-consulted every load, and until
 * it answers `getSubUpdateArm()` falls through to the build default — which is `false`, i.e.
 * CONTROL. An enrolled arm-b account was therefore served control on every page load until the
 * round trip finished, and for the WHOLE session whenever that call returned null or threw.
 *
 * Caching the answer against the account id closes both windows: a reload starts on the arm this
 * account already got, and a failed resolve no longer silently demotes anyone.
 *
 * Keyed by account so it can never leak across a logout into someone else's session, and held in
 * sessionStorage rather than localStorage for the same reason the QA pin is — a stale arm that
 * outlives the tab is how an experiment silently stops splitting.
 */
const DECIDED_CACHE_KEY = 'scio3_sub_update_decided';

/**
 * Arm names are the contract, but four other spellings are accepted, all meaning the experiment
 * arm:
 *   - `treatment` — what the OptiGrowth dashboard PRE-FILLS its second variant with. Accepting it
 *     removes the one failure mode nothing else catches: an experiment named `control`/`treatment`
 *     would otherwise serve control to every enrolled company while the dashboard reported a
 *     healthy 50/50, so the split looks perfect and both groups saw the same UI.
 *   - `a` (this arm's name before the rename) and `1`/`true` (what the even earlier `?billingExp`
 *     wrote). Without them, every browser already pinned to `a` — including every reviewer's —
 *     would silently snap back to control and look like the experiment had vanished.
 */
function parseArm(raw: string | null): SubUpdateArm | null {
  if (raw === null) return null;
  const value = raw.trim().toLowerCase();
  if (
    value === SUB_UPDATE_ARMS.B ||
    value === 'treatment' ||
    value === 'a' ||
    value === '1' ||
    value === 'true'
  ) {
    return SUB_UPDATE_ARMS.B;
  }
  if (value === SUB_UPDATE_ARMS.CONTROL || value === '0' || value === 'false') {
    return SUB_UPDATE_ARMS.CONTROL;
  }
  // Unknown arm (a typo, or an arm that does not exist yet) — fall through to the default
  // rather than guessing, so `?sub-update=c` never quietly serves arm b.
  return null;
}

/**
 * URL → sessionStorage sync. Done once at import time, not inside the getter, so
 * `isBillingExperimentOn()` stays a pure read and can be called during render.
 */
/**
 * Non-prod only, matching `readForcedVariant` (the generic decider override). A pinned arm in
 * production would put a real user on a chosen arm and count them in the readout — an experiment
 * measuring a group that was never randomised.
 */
function isOverrideAllowed(): boolean {
  return import.meta.env.VITE_POSTHOG_ENVIRONMENT !== 'production';
}

function persistUrlOverride(): void {
  if (!isOverrideAllowed()) return;
  try {
    const arm = parseArm(new URLSearchParams(window.location.search).get(QUERY_PARAM));
    if (arm === null) return;
    sessionStorage.setItem(STORAGE_KEY, arm);
  } catch {
    // Private-mode storage throws. Fall through to the env default.
  }
}

/**
 * Delete any pin left in localStorage by the previous implementation.
 *
 * Without this, a browser that pinned an arm before the move to sessionStorage keeps a key that
 * nothing reads — inert, but it is the first thing anyone debugging "the split is not splitting"
 * will find, and it looks like it is still in play. Clearing it means the trap is gone for everyone
 * who ever hit it, not just for new sessions.
 */
function migrateLegacyPin(): void {
  try {
    if (localStorage.getItem(STORAGE_KEY) !== null) {
      localStorage.removeItem(STORAGE_KEY);
      console.warn(
        `[sub-update-v1] removed a stale localStorage.${STORAGE_KEY} pin. The QA override is ` +
          `sessionStorage now, so it no longer outlives the tab. Re-pin with ?${QUERY_PARAM}=b if you meant to.`,
      );
    }
  } catch {
    // No storage access — nothing to migrate.
  }
}

migrateLegacyPin();
persistUrlOverride();

/**
 * The decider's answer, once. Module state rather than a store because every consumer is
 * a synchronous read during render and there is exactly one value per session.
 * `null` until `useBillingExperimentArm` resolves, and also after it resolves for a
 * company that is NOT enrolled — the decider returns null for ineligible, held-out and
 * unknown-key, none of which mean "control".
 */
let decidedArm: SubUpdateArm | null = null;

/**
 * Called by `useBillingExperimentArm` with whatever `resolveExperimentVariant` returned.
 *
 * An unrecognised arm string is LOUD rather than silent. The dashboard experiment names its
 * own arms, and a mismatch (`treatment` where this expects `b`) would otherwise serve every
 * enrolled user control while the dashboard reported a healthy 50/50 — an experiment that
 * looks like it worked and measured nothing. Better a console warning someone can find.
 */
export function setDecidedArm(raw: string | null): void {
  const arm = parseArm(raw);
  if (raw !== null && arm === null) {
    console.warn(
      `[sub-update-v1] decider returned an unknown arm "${raw}" — expected ` +
        `"${SUB_UPDATE_ARMS.CONTROL}" or "${SUB_UPDATE_ARMS.B}". Falling back to the build default.`,
    );
  }
  // A null is "no answer" — not "control". The decider returns null for ineligible, held-out,
  // unknown-key AND for its own failures, so letting one overwrite a known arm is what moved a
  // settled arm-b account onto control mid-session.
  if (arm === null && decidedArm !== null) return;
  decidedArm = arm;
  stampArm();
}

/**
 * Seed the arm this account resolved to earlier, so a reload does not start on the build default
 * while the decider is in flight. Never overrides an arm already decided this page load.
 */
export function seedDecidedArm(accountId: string | undefined): void {
  if (!accountId || decidedArm !== null) return;
  try {
    const raw = sessionStorage.getItem(DECIDED_CACHE_KEY);
    if (!raw) return;
    const [cachedAccount, cachedArm] = raw.split(':');
    if (cachedAccount !== accountId) return;
    const arm = parseArm(cachedArm ?? null);
    if (arm === null) return;
    decidedArm = arm;
    stampArm();
  } catch {
    // No storage access — fall through to the decider.
  }
}

/** Remember a resolved arm for this account. Only ever called with a real arm. */
export function rememberDecidedArm(accountId: string | undefined, arm: string | null): void {
  const parsed = parseArm(arm);
  if (!accountId || parsed === null) return;
  try {
    sessionStorage.setItem(DECIDED_CACHE_KEY, `${accountId}:${parsed}`);
  } catch {
    // Private-mode storage throws — the arm still holds for this page load.
  }
}

/** A reviewer's pinned arm, if any. Read by `useBillingExperimentArm`: with one set there is
 *  nothing to wait for, so the consumers must not hold. */
export function hasPinnedArm(): boolean {
  try {
    return parseArm(sessionStorage.getItem(STORAGE_KEY)) !== null;
  } catch {
    return false;
  }
}

/**
 * Warn, once per page load, whenever a pinned arm is what is being served.
 *
 * The pin is invisible, permanent and beats the decider, so a stale one silently masks the real
 * experiment: every account on that browser profile shows the pinned arm while the dashboard
 * reports a healthy split. That is exactly what happened on qa3g — hours spent wondering why the
 * split "was not working" when a `?sub-update=b` visit weeks earlier had written the pin.
 *
 * `readForcedVariant` (the generic decider override) has always logged when it forces an arm. This
 * is the same courtesy for this one. Mirrors its wording so both are greppable.
 */
let pinWarned = false;
function warnPinned(arm: SubUpdateArm): void {
  if (pinWarned) return;
  pinWarned = true;
  console.warn(
    `[sub-update-v1] QA override: serving "${arm}" from sessionStorage.${STORAGE_KEY}, which ` +
      `BEATS the decider. Close the tab, or sessionStorage.removeItem('${STORAGE_KEY}'), to see ` +
      `the real arm.`,
  );
}

function getSubUpdateArm(): SubUpdateArm {
  try {
    const stored = parseArm(sessionStorage.getItem(STORAGE_KEY));
    if (stored !== null) {
      warnPinned(stored);
      return stored;
    }
  } catch {
    // Ignore and fall through.
  }
  if (decidedArm !== null) return decidedArm;
  return import.meta.env.VITE_ENABLE_BILLING_EXPERIMENT === 'true'
    ? SUB_UPDATE_ARMS.B
    : SUB_UPDATE_ARMS.CONTROL;
}

/**
 * Stamp the resolved arm on <html> so "which arm am I looking at?" is answerable.
 * The two arms start byte-identical, so without this there is nothing on screen or in
 * the DOM that tells them apart. Read it in DevTools as `document.documentElement.dataset
 * .subUpdateArm`, or in Playwright via `html[data-sub-update-arm="b"]`.
 *
 * Deliberately here and not on the copied components: the copies stay verbatim, so
 * diffing them against production shows only what the experiment actually changed.
 *
 * Re-stamped by `setDecidedArm`, so the attribute tracks the arm actually being served
 * rather than the build default that answered before the decider settled.
 */
function stampArm(): void {
  try {
    document.documentElement.dataset.subUpdateArm = getSubUpdateArm();
  } catch {
    // No DOM (unit tests / SSR) — nothing to stamp.
  }
}

export function isBillingExperimentOn(): boolean {
  return getSubUpdateArm() === SUB_UPDATE_ARMS.B;
}

stampArm();
